/**
 * Concern:
 * Shared structured logging utilities for Lambda entrypoints and queue workers.
 * Owns JSON log shaping, error serialization, and reusable SQS-record processing wrappers.
 */

type JsonLogValue =
  | string
  | number
  | boolean
  | null
  | JsonLogValue[]
  | { [key: string]: JsonLogValue | undefined }
  | undefined;

type LogFields = Record<string, JsonLogValue>;

type LogSink = {
  error: (message?: unknown) => void;
  info: (message?: unknown) => void;
  warn: (message?: unknown) => void;
};

type StructuredLogLevel = "ERROR" | "INFO" | "WARN";

type StructuredLogRecord = LogFields & {
  eventType: string;
  level: StructuredLogLevel;
  timestamp: string;
};

type SqsRecordLike = {
  attributes?: {
    ApproximateReceiveCount?: string;
  };
  body: string;
  eventSourceARN?: string;
  messageId?: string;
};

type SqsEventLike = {
  Records: readonly SqsRecordLike[];
};

const defaultLogSink: LogSink = {
  error: console.error,
  info: console.info,
  warn: console.warn,
};

const cleanLogFields = (fields: LogFields): LogFields =>
  Object.fromEntries(
    Object.entries(fields).filter(([, value]) => value !== undefined),
  );

const parseAttempt = (receiveCount: string | undefined) => {
  if (!receiveCount) {
    return undefined;
  }

  const attempt = Number.parseInt(receiveCount, 10);
  return Number.isNaN(attempt) ? undefined : attempt;
};

const parseQueueName = (eventSourceArn: string | undefined) => {
  if (!eventSourceArn) {
    return undefined;
  }

  const parts = eventSourceArn.split(":");
  return parts.length > 0 ? parts.at(-1) : undefined;
};

const serializeUnknownError = (error: unknown): LogFields => {
  if (error instanceof Error) {
    const errorCode =
      "code" in error &&
      (typeof error.code === "string" || typeof error.code === "number")
        ? String(error.code)
        : undefined;

    return cleanLogFields({
      errorCode,
      errorMessage: error.message,
      errorName: error.name,
      errorStack: error.stack,
    });
  }

  if (typeof error === "object" && error) {
    const errorRecord = error as Record<string, unknown>;
    return cleanLogFields({
      errorCode:
        typeof errorRecord.code === "string" ||
        typeof errorRecord.code === "number"
          ? String(errorRecord.code)
          : undefined,
      errorMessage:
        typeof errorRecord.message === "string"
          ? errorRecord.message
          : JSON.stringify(errorRecord),
      errorName:
        typeof errorRecord.name === "string" ? errorRecord.name : undefined,
    });
  }

  return {
    errorMessage: String(error),
  };
};

const writeStructuredLog = (
  sink: LogSink,
  level: StructuredLogLevel,
  eventType: string,
  baseFields: LogFields,
  fields: LogFields = {},
  error?: unknown,
) => {
  const payload: StructuredLogRecord = {
    ...cleanLogFields(baseFields),
    ...cleanLogFields(fields),
    ...(error ? serializeUnknownError(error) : {}),
    eventType,
    level,
    timestamp: new Date().toISOString(),
  };

  const serialized = JSON.stringify(payload);
  if (level === "ERROR") {
    sink.error(serialized);
    return;
  }

  if (level === "WARN") {
    sink.warn(serialized);
    return;
  }

  sink.info(serialized);
};

export type StructuredLogger = {
  child: (fields: LogFields) => StructuredLogger;
  error: (eventType: string, error?: unknown, fields?: LogFields) => void;
  info: (eventType: string, fields?: LogFields) => void;
  warn: (eventType: string, fields?: LogFields) => void;
};

export const createStructuredLogger = (
  baseFields: LogFields,
  sink: Partial<LogSink> = {},
): StructuredLogger => {
  const resolvedSink: LogSink = {
    ...defaultLogSink,
    ...sink,
  };

  const cleanedBaseFields = cleanLogFields(baseFields);

  return {
    child: (fields) =>
      createStructuredLogger(
        {
          ...cleanedBaseFields,
          ...cleanLogFields(fields),
        },
        resolvedSink,
      ),
    error: (eventType, error, fields) =>
      writeStructuredLog(
        resolvedSink,
        "ERROR",
        eventType,
        cleanedBaseFields,
        fields,
        error,
      ),
    info: (eventType, fields) =>
      writeStructuredLog(
        resolvedSink,
        "INFO",
        eventType,
        cleanedBaseFields,
        fields,
      ),
    warn: (eventType, fields) =>
      writeStructuredLog(
        resolvedSink,
        "WARN",
        eventType,
        cleanedBaseFields,
        fields,
      ),
  };
};

export const buildSqsRecordLogContext = (record: SqsRecordLike): LogFields =>
  cleanLogFields({
    attempt: parseAttempt(record.attributes?.ApproximateReceiveCount),
    messageId: record.messageId,
    queueName: parseQueueName(record.eventSourceARN),
  });

export const createLambdaLogger = ({
  awsRequestId,
  functionName,
  service,
  workflowType,
  sink,
}: {
  awsRequestId?: string;
  functionName?: string;
  service: string;
  sink?: Partial<LogSink>;
  workflowType?: string;
}) =>
  createStructuredLogger(
    cleanLogFields({
      awsRequestId,
      functionName,
      service,
      workflowType,
    }),
    sink,
  );

export const processLoggedSqsRecords = async <TMessage>({
  awsRequestId,
  event,
  functionName,
  getMessageLogContext,
  getNow = () => Date.now(),
  handleMessage,
  parseMessage,
  service,
  sink,
  workflowType,
}: {
  awsRequestId?: string;
  event: SqsEventLike;
  functionName?: string;
  getMessageLogContext?: (message: TMessage) => LogFields;
  getNow?: () => number;
  handleMessage: (
    message: TMessage,
    record: SqsRecordLike,
    logger: StructuredLogger,
  ) => Promise<void>;
  parseMessage: (record: SqsRecordLike) => TMessage;
  service: string;
  sink?: Partial<LogSink>;
  workflowType: string;
}) => {
  const startedAt = getNow();
  const logger = createLambdaLogger({
    awsRequestId,
    functionName,
    service,
    sink,
    workflowType,
  });

  logger.info("workflow.started", {
    recordCount: event.Records.length,
  });

  try {
    for (const record of event.Records) {
      const recordLogger = logger.child(buildSqsRecordLogContext(record));
      let message: TMessage;
      try {
        message = parseMessage(record);
      } catch (error) {
        recordLogger.error("workflow.failed", error, {
          outcome: "failure",
          phase: "parse",
        });
        throw error;
      }

      const messageLogger = recordLogger.child(
        cleanLogFields(getMessageLogContext?.(message) ?? {}),
      );
      const messageStartedAt = getNow();

      messageLogger.info("queue.message.received", {
        outcome: "received",
      });

      try {
        await handleMessage(message, record, messageLogger);
        messageLogger.info("workflow.progress", {
          durationMs: getNow() - messageStartedAt,
          outcome: "success",
        });
      } catch (error) {
        messageLogger.error("workflow.failed", error, {
          durationMs: getNow() - messageStartedAt,
          outcome: "failure",
          phase: "handle",
        });
        throw error;
      }
    }

    logger.info("workflow.completed", {
      durationMs: getNow() - startedAt,
      outcome: "success",
      recordCount: event.Records.length,
    });
  } catch (error) {
    logger.error("workflow.failed", error, {
      durationMs: getNow() - startedAt,
      outcome: "failure",
      recordCount: event.Records.length,
    });
    throw error;
  }
};
